// Mail: the inbox, triage, scheduled sending, templates and list sends. Shapes shared by the server and the browser,
// plus the pure rules both sides must agree on (merge fields, daily limits, list send pacing).

import type { CompanyRef, Ref, StageColor } from './types';

/* ---------- addresses and messages ---------- */

export interface MailAddress {
  email: string;
  name: string | null;
}

export interface EmailAttachment {
  name: string;
  size: number;
  mime: string;
  gmailAttachmentId?: string | null;
}

export type MailDirection = 'in' | 'out';
export type MailVia = 'gmail' | 'crm' | 'forward' | 'copy' | 'shared';
export type MailboxKind = 'personal' | 'shared' | 'crm';

/** What an open count is made of, for the tooltip wherever opens show. The rules live in ignoreOpen (mail/tokens.ts). */
export const OPENS_EXPLAINED =
  'A hint from image loads a minute or more after sending. Loads by scanners and from the sender’s own networks are left out. Some mail apps load images on arrival and others block them. The sender viewing it in Gmail counts.';

export interface EmailMessage {
  id: string;
  threadId: string;
  direction: MailDirection;
  via: MailVia;
  from: MailAddress;
  to: MailAddress[];
  cc: MailAddress[];
  subject: string;
  snippet: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: EmailAttachment[];
  sentAt: string;
  /** The teammate who wrote it (for messages sent by the team). */
  sentBy: Ref | null;
  /** The teammate who forwarded or copied it to the CRM address. */
  forwardedBy: Ref | null;
  trackOpens: boolean;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  /** Avatar colour of the sender: the teammate's hue, the contact's hue, or a hue from the address. */
  hue: number;
  rfcMessageId: string | null;
  /** The email this one forwards, when it was forwarded from Clientbook. */
  forwardOf: ForwardOrigin | null;
}

/** The original email a forward passes on: "Forward of “Revised scope” from Hannah Whitcombe". */
export interface ForwardOrigin {
  id: string;
  threadId: string;
  subject: string;
  /** The original sender's name, else their address. */
  from: string;
}

/* ---------- inbox ---------- */

export type InboxFilter = 'all' | 'mine' | 'shared' | 'triage' | 'scheduled';

export const INBOX_FILTERS: { id: InboxFilter; label: string }[] = [
  { id: 'all', label: 'All tracked' },
  { id: 'mine', label: 'Mine' },
  { id: 'shared', label: 'Shared inboxes' },
  { id: 'triage', label: 'Triage' },
  { id: 'scheduled', label: 'Scheduled' },
];

export interface InboxCounts {
  all: number;
  mine: number;
  shared: number;
  triage: number;
  scheduled: number;
  /** Replies waiting in your own conversations (the dot on the Inbox nav item). */
  unreadMine: number;
}

export type ThreadSourceKind = 'personal' | 'shared' | 'forward' | 'copy' | 'crm';

export interface ThreadSource {
  kind: ThreadSourceKind;
  /** "Maya’s inbox", "hello@", "Forwarded by Jonas". */
  label: string;
}

export interface DealRef {
  id: string;
  name: string;
  stageName: string | null;
  stageColor: StageColor | null;
}

export interface ThreadListItem {
  id: string;
  subject: string;
  snippet: string;
  lastMessageAt: string;
  lastDirection: MailDirection | null;
  messageCount: number;
  unread: boolean;
  /** Unread and the team wrote first: a contact answered. */
  newReply: boolean;
  /** Days since the team's last message when the contact has not answered for a while. */
  quietDays: number | null;
  /** The external party shown on the row. */
  contact: { name: string; email: string | null; hue: number };
  person: Ref | null;
  company: CompanyRef | null;
  deal: DealRef | null;
  source: ThreadSource;
  startedFromCrm: boolean;
  /** Open hints on the team's tracked messages in the conversation. */
  opens: number;
  /** Who on the team wrote the latest message, when the team wrote it. */
  lastSender: string | null;
}

export interface ThreadPage {
  items: ThreadListItem[];
  counts: InboxCounts;
  nextCursor: string | null;
}

export interface ThreadMailbox {
  id: string;
  address: string;
  kind: MailboxKind;
  displayName: string | null;
}

export interface ThreadDetail {
  id: string;
  subject: string;
  mailbox: ThreadMailbox | null;
  person: (Ref & { email: string | null }) | null;
  company: CompanyRef | null;
  deal: DealRef | null;
  source: ThreadSource;
  startedFromCrm: boolean;
  messages: EmailMessage[];
  /** A waiting triage item for a message in this conversation, if any. */
  triageId: string | null;
  lastDirection: MailDirection | null;
  lastMessageAt: string;
  /** The latest time the team sent a message in it from Clientbook. */
  crmSentAt: string | null;
  quietDays: number | null;
  unread: boolean;
  /** Hidden from your list until then (or until a new message arrives). */
  snoozedUntil: string | null;
  /** Open hints are recorded on the team's messages here. */
  tracking: boolean;
}

/* ---------- triage ---------- */

export type TriageStatus = 'waiting' | 'reviewed' | 'dismissed';
export type TriageVia = 'forward' | 'copy' | 'shared';

export interface TriageListItem {
  id: string;
  emailId: string;
  threadId: string;
  status: TriageStatus;
  kind: 'known' | 'unknown';
  via: TriageVia;
  submittedBy: Ref | null;
  /** "Forwarded by Jonas", "Copied by Maya", "hello@". */
  sourceLabel: string;
  contact: { name: string | null; email: string | null; hue: number };
  person: Ref | null;
  company: Ref | null;
  subject: string;
  snippet: string;
  receivedAt: string;
  suggestDismissed: boolean;
  analysed: boolean;
}

export type TriageSuggestionType =
  | 'add_person'
  | 'add_company'
  | 'set_role'
  | 'file_person'
  | 'file_company'
  | 'file_deal'
  | 'followup';

export interface TriageSuggestion {
  id: string;
  type: TriageSuggestionType;
  /** "Add Sofia Brandt as a contact". */
  label: string;
  /** "Sofia Brandt · Head of Digital, Arcadia Health · sofia.brandt@arcadia-health.de". */
  detail: string;
  person?: { name: string; title: string | null; email: string | null } | null;
  company?: { name: string; domain: string | null; industry: string | null; staff: number | null } | null;
  title?: string | null;
  recordId?: string | null;
  /** A follow-up to add, due on a calendar day (YYYY-MM-DD). */
  followup?: { title: string; due: string } | null;
}

export interface TriageDecision {
  state: 'accepted' | 'skipped';
  /** What accepting created or linked, so the card can open it. */
  result?: { type: 'person' | 'company' | 'deal' | 'followup'; id: string; name: string } | null;
  by: string;
  at: string;
}

export interface TriageRun {
  model: string;
  modelName: string;
  effort: string;
  costUsd: number;
  seconds: number | null;
}

export interface TriageAi {
  summary: string;
  /** How the contact found the team, when the email says so ("Referral · Sven de Groot (Brightwater Logistics)"). */
  source: string | null;
  suggestions: TriageSuggestion[];
  run: TriageRun | null;
}

export interface TriageEstimate {
  modelName: string;
  effort: string;
  costUsd: number;
}

export interface TriageDetail extends TriageListItem {
  message: EmailMessage;
  /** Earlier messages in the same conversation, oldest first. */
  earlier: EmailMessage[];
  ai: TriageAi | null;
  decisions: Record<string, TriageDecision>;
  /** Null when AI is not connected. */
  estimate: TriageEstimate | null;
  /** When the sender is unknown: what Add contact fills in. */
  proposed: { name: string; email: string | null; companyId: string | null; companyName: string | null; domain: string | null } | null;
}

/* ---------- rejected forwards (written by the Gmail area, shown here) ---------- */

export interface RejectedForward {
  id: string;
  fromAddr: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
}

/* ---------- outbox and list sends ---------- */

export type OutboxStatus = 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';

export interface OutboxItem {
  id: string;
  status: OutboxStatus;
  sendAt: string;
  subject: string;
  snippet: string;
  bodyHtml: string;
  bodyText: string;
  to: MailAddress[];
  cc: MailAddress[];
  bcc: MailAddress[];
  attachments: OutboxAttachment[];
  mailbox: ThreadMailbox | null;
  createdBy: Ref | null;
  person: Ref | null;
  company: Ref | null;
  deal: { id: string; name: string } | null;
  threadId: string | null;
  replyToEmailId: string | null;
  forwardOf: ForwardOrigin | null;
  /** The conversation the email went into, once sent. */
  sentThreadId: string | null;
  templateId: string | null;
  trackOpens: boolean;
  attempts: number;
  /** Why the last try did not send: the reason it failed, or why it waits for a retry at sendAt. */
  error: string | null;
  mine: boolean;
}

export interface SkippedPerson {
  personId: string;
  name: string;
  company: string | null;
  hue: number;
  /** "Do not email", "Unsubscribed", "No email address", "Team member", "Sample contact", "Same address twice". */
  reason: string;
  detail: string | null;
}

export type ListSendStatus = 'scheduled' | 'sending' | 'done' | 'cancelled';

export interface ListSendItem {
  id: string;
  status: ListSendStatus;
  subject: string;
  bodyHtml: string;
  listName: string | null;
  mailbox: ThreadMailbox | null;
  createdBy: Ref | null;
  startsAt: string;
  intervalSeconds: number;
  total: number;
  sent: number;
  failed: number;
  cancelled: number;
  waiting: number;
  nextAt: string | null;
  finishAt: string | null;
  skipped: SkippedPerson[];
  trackOpens: boolean;
  opens: number;
  mine: boolean;
}

/** One personal copy of a list send. */
export interface ListCopy {
  outboxId: string;
  personId: string | null;
  name: string;
  email: string;
  status: OutboxStatus;
  sendAt: string;
  error: string | null;
  openCount: number;
}

export interface ListSendDetail extends ListSendItem {
  copies: ListCopy[];
}

export type ScheduledEntry = ({ kind: 'email' } & OutboxItem) | ({ kind: 'list' } & ListSendItem);

/* ---------- templates ---------- */

export interface Template {
  id: string;
  name: string;
  subject: string;
  /** HTML, with {first} and {company} merge fields. */
  body: string;
  createdBy: Ref | null;
  updatedAt: string;
  uses: number;
}

/* ---------- composing ---------- */

export interface ComposeMailbox extends ThreadMailbox {
  canSend: boolean;
  /** Why this inbox cannot send, in plain words. */
  problem: string | null;
  dailyLimit: number;
  /** Emails this inbox sent from Clientbook in the last 24 hours, and the ones going out right now. */
  sentToday: number;
  /** Emails from this inbox scheduled within a day of now (list copies, scheduled and retrying emails). */
  waiting: number;
}

/** What already counts against an inbox's daily limit for a list send starting at a later time. */
export interface ListSendRoom {
  /** Sent in the 24 hours before the start, and going out right now. */
  sentToday: number;
  /** Scheduled within a day either side of the start. */
  waiting: number;
}

export interface ComposeInfo {
  /** Google sign-in is set up, so inboxes can be connected. */
  google: boolean;
  mailboxes: ComposeMailbox[];
  /** The address to use when nothing else is chosen: your own inbox, else the first shared one. */
  defaultMailboxId: string | null;
  maxAttachmentBytes: number;
}

export interface ContactSuggestion {
  personId: string | null;
  name: string;
  email: string;
  company: string | null;
  hue: number;
  /** Set when Clientbook refuses to send to this address, with the reason. */
  problem: string | null;
}

/** A new email written from a record: who it goes to and the records it is filed on. */
export interface ComposeTarget {
  to: ContactSuggestion[];
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
  /** The person's name, for the composer title. */
  name: string | null;
}

export interface ComposeRecipientInput {
  email: string;
  name: string | null;
  personId?: string | null;
}

export interface ComposeInput {
  mailboxId: string;
  to: ComposeRecipientInput[];
  cc: ComposeRecipientInput[];
  bcc: ComposeRecipientInput[];
  subject: string;
  html: string;
  text: string;
  attachments: AttachmentRef[];
  templateId: string | null;
  trackOpens: boolean;
  /** Null sends after the short undo window. */
  sendAt: string | null;
  replyToEmailId: string | null;
  forwardOfEmailId: string | null;
  threadId: string | null;
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
  /** Replaces this scheduled email instead of adding a new one. */
  outboxId: string | null;
  /** The writer's time zone, for the retry time in a "not sent yet" notification. */
  timeZone: string;
}

export interface ComposeResult {
  outboxIds: string[];
  sendAt: string;
  scheduled: boolean;
  /** True when each To recipient gets their own copy (merge fields with several recipients). */
  personal: boolean;
}

/** A file in an email being written: uploaded in the composer, or kept from the email being forwarded. */
export type AttachmentRef = { kind: 'upload'; id: string } | { kind: 'email'; emailId: string; attachmentId: string };

export type OutboxAttachment = AttachmentRef & { name: string; size: number; mime: string };

/** What the composer needs to answer or pass on a message. */
export interface ReplyContext {
  mode: 'reply' | 'replyAll' | 'forward';
  mailboxId: string | null;
  to: ComposeRecipientInput[];
  cc: ComposeRecipientInput[];
  subject: string;
  parent: EmailMessage;
  replyToEmailId: string | null;
  threadId: string;
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
  /** Files of the forwarded message, passed on unless removed. */
  attachments: OutboxAttachment[];
  /** The earlier message, quoted under a reply or passed on in a forward. */
  quote: { html: string; text: string };
  /** Whether the reply starts with open tracking on; off once the team stopped tracking the conversation. */
  trackOpens: boolean;
}

/** How long "Send" waits before the email leaves, so it can be undone. */
export const UNDO_SECONDS = 10;

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  mime: string;
}

/** Gmail's limit for one message, attachments included. */
export const MAX_MESSAGE_BYTES = 25 * 1024 * 1024;

/* ---------- list sends ---------- */

export interface ListRecipient {
  personId: string;
  name: string;
  email: string;
  company: string | null;
  hue: number;
}

export interface ListSendPreview {
  mailboxes: ComposeMailbox[];
  defaultMailboxId: string | null;
  recipients: ListRecipient[];
  skipped: SkippedPerson[];
  requested: number;
}

export interface ListSendInput {
  personIds: string[];
  listName: string | null;
  mailboxId: string;
  subject: string;
  html: string;
  text: string;
  templateId: string | null;
  trackOpens: boolean;
  startAt: string | null;
}

/* ---------- merge fields ---------- */

export const MERGE_FIELDS = ['{first}', '{company}'] as const;

const MERGE_RE = /\{(first|first_name|company|my_first_name)\}/g;

export function hasMergeFields(text: string): boolean {
  return /\{(first|first_name|company|my_first_name)\}/.test(text);
}

/** "Dr. Lena Hoffmann" → "Lena"; an address's local part ("hannah.whitcombe") → "Hannah". */
export function firstName(name: string | null | undefined, email?: string | null): string | null {
  const clean = (name ?? '').replace(/^\s*(dr|mr|mrs|ms|mx|prof|sir)\.?\s+/i, '').trim();
  if (clean && !clean.includes('@')) {
    const word = clean.split(/\s+/)[0]!;
    if (/\p{L}/u.test(word)) return word;
  }
  const local = (email ?? '').split('@')[0] ?? '';
  const part = local.split(/[._+-]/)[0] ?? '';
  if (part.length > 1 && /^[a-z]+$/i.test(part)) return part[0]!.toUpperCase() + part.slice(1).toLowerCase();
  return null;
}

export interface MergeValues {
  first: string | null;
  company: string | null;
  myFirst?: string | null;
}

/** What a merge field becomes when the record has no value for it. */
export const MERGE_FALLBACKS = { first: 'there', company: 'your team' } as const;

function escapeHtmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Fills {first} and {company} (and {my_first_name}) for one recipient. Pass html for HTML bodies so values are escaped. */
export function fillMergeFields(text: string, values: MergeValues, mode: 'text' | 'html' = 'text'): string {
  return text.replace(MERGE_RE, (_m, key: string) => {
    let v: string;
    if (key === 'first' || key === 'first_name') v = values.first?.trim() || MERGE_FALLBACKS.first;
    else if (key === 'company') v = values.company?.trim() || MERGE_FALLBACKS.company;
    else v = values.myFirst?.trim() || '';
    return mode === 'html' ? escapeHtmlText(v) : v;
  });
}

/* ---------- daily limits and pacing ---------- */

/** Gmail's rolling 24-hour sending limit: 500 for consumer Gmail addresses, 2,000 for Google Workspace. */
export function dailyLimit(address: string): number {
  const domain = address.split('@')[1]?.toLowerCase() ?? '';
  return domain === 'gmail.com' || domain === 'googlemail.com' ? 500 : 2000;
}

/** The shortest gap between two copies of a list send, so it reads like one-to-one mail. */
export const MIN_GAP_SECONDS = 90;

/**
 * The average gap between copies: never under 90 seconds, and wide enough that a full day of copies stays inside the
 * inbox's daily limit (2,000 a day allows one every 43 s, so Workspace inboxes use 90 s; 500 a day needs 173 s).
 */
export function listInterval(limit: number): number {
  return Math.max(MIN_GAP_SECONDS, Math.ceil(86_400 / Math.max(1, limit)));
}

/** A stable wobble of up to ±25% per copy, so copies do not leave on an exact beat. */
export function jitterSeconds(index: number, interval: number): number {
  if (index === 0) return 0;
  const x = Math.sin(index * 12.9898 + 78.233) * 43758.5453;
  const frac = x - Math.floor(x);
  return Math.round((frac - 0.5) * 0.5 * interval);
}

export interface PacingInput {
  start: Date;
  count: number;
  limit: number;
  /** Emails that already count against the limit in the first day: sent, going out, or scheduled within a day of the start. */
  usedToday: number;
}

export interface Pacing {
  interval: number;
  times: Date[];
  /** Copies that wait for a later day because the inbox would reach its limit. */
  overflow: number;
  finish: Date | null;
}

const DAY_MS = 86_400_000;

/**
 * When each copy of a list send goes out. The first day takes as many copies as the inbox has room for; the rest go
 * in later days of up to a full limit each. Each later day starts when the rolling 24-hour window has moved past the
 * day before (24 hours after that day began), and is spaced like the first, so no 24 hours hold more than the limit.
 */
export function planListSend(p: PacingInput): Pacing {
  const interval = listInterval(p.limit);
  const room = Math.max(0, p.limit - p.usedToday);
  const times: Date[] = [];
  let dayStart = p.start.getTime();
  let cap = room;
  let inDay = 0;
  let prev: number | null = null;
  for (let i = 0; i < p.count; i++) {
    while (inDay >= cap) {
      dayStart = Math.max(dayStart + DAY_MS, prev === null ? 0 : prev + interval * 1000);
      cap = Math.max(1, p.limit);
      inDay = 0;
    }
    const t = dayStart + (inDay * interval + jitterSeconds(inDay, interval)) * 1000;
    times.push(new Date(t));
    prev = t;
    inDay++;
  }
  return { interval, times, overflow: Math.max(0, p.count - room), finish: times.length ? times[times.length - 1]! : null };
}

/** How many days after the first the overflow copies take. */
export function overflowDays(overflow: number, limit: number): number {
  return overflow > 0 ? Math.ceil(overflow / Math.max(1, limit)) : 0;
}

/* ---------- templates ---------- */

/**
 * A free name for a template coming back from the bin: its own name when no live template uses it, else
 * "Name (restored)", then "Name (restored 2)" and so on. A name that already ends in "(restored)" or
 * "(restored N)" gets a new number instead of a second suffix. `taken` holds the live names in lower case.
 */
export function restoredName(name: string, taken: Set<string>, max = 80): string {
  if (!taken.has(name.toLowerCase())) return name;
  const base = name.replace(/ \(restored(?: \d+)?\)$/, '');
  for (let n = 1; ; n++) {
    const suffix = n === 1 ? ' (restored)' : ` (restored ${n})`;
    const candidate = `${base.slice(0, Math.max(0, max - suffix.length)).trimEnd()}${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** "One every ~1.5 min", "One every ~3 min". */
export function intervalLabel(seconds: number): string {
  if (seconds < 60) return `One every ~${seconds} s`;
  const min = seconds / 60;
  const shown = min < 10 ? Math.round(min * 2) / 2 : Math.round(min);
  return `One every ~${String(shown).replace(/\.0$/, '')} min`;
}

/** "About 45 minutes", "About 2 hours", "About 1.5 hours". */
export function durationLabel(ms: number): string {
  const min = Math.max(1, Math.round(ms / 60_000));
  if (min < 60) return `About ${min} ${min === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round((min / 60) * 2) / 2;
  if (hours < 24) return `About ${String(hours).replace(/\.0$/, '')} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  return `About ${days} ${days === 1 ? 'day' : 'days'}`;
}
